/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useMemo, useState } from 'react';

import { DeviceModel, Settings } from '../../../../core/device';
import { ErrorTypes } from '../../../../core/error';
import { NdvrInteractor } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { Log } from '../../../../core/logging';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../../core/navigation';
import { TScreen } from '../../../../core/navigation/types';
import { NetworkManager } from '../../../../core/network/NetworkManager';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { showEmptyRecordingActivityScreen } from '../../../../store/screens/screens.reducers';
import {
    setRecordingActivityApiErrorAction,
    setRecordingActivityDataAction,
    setTotalCountRecordingActivityAction,
} from '../../../../store/settings/recordingactivity/recordingactivity.actions';
import {
    disableGhostModeRecordingActivity,
    resetRecordingActivityState,
    unsubscribeFromUIOrchestratorRecordingActivity,
} from '../../../../store/settings/recordingactivity/recordingactivity.reducers';
import { showWhisper } from '../../../../store/whisper/whisper.reducer';
import { AntiburnOsd, Mousetrap } from '../../../common';
import { useScreenController } from '../../../hooks';
import { NoNetworkOverlay } from '../../../NoNetworkOverlay';
import { WhisperTypes } from '../../../Whisper/WhisperTypes';
import { SettingsTemplate } from '../../SettingsTemplate';
import { Footer } from '../Footer/Footer';
import {
    getRecordingItemsFromPage,
    LIST_ITEMS_MAX_COUNT,
    LIST_ROWS_ON_PAGE,
} from './RecordingActivity.utils';
import RecordingActivityContent from './RecordingActivityContent/RecordingActivityContent';
import RecordingActivityInteractiveLayer from './RecordingActivityInteractiveLayer/RecordingActivityInteractiveLayer';
import RecordingActivityPreview from './RecordingActivityPreview/RecordingActivityPreview';

import './RecordingActivity.scss';

const TAG = 'RecordingActivityScreen';

const handleRecordingActivityError = (dispatch: Function, error: unknown) => {
    Log.e(TAG, 'Error fetching recording activity content:', error);
    const whisperMessage = Locale.getString(
        LocaleKeys.RECORDING_ACTIVITY_SCREEN_WHISPER_MESSAGE,
    ).replace('ERROR_CODE', ErrorTypes.RECORDING_ACTIVITY_APIGW_5XX_C2402);
    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.NO_ICON,
        }),
    );
    dispatch(setRecordingActivityApiErrorAction(true));
    dispatch(resetRecordingActivityState());
};

const RecordingActivity = () => {
    const [isCursorActive, setIsCursorActive] = useState(false);
    const settingsService = new Settings();
    const isVideoWindowEnabled = settingsService.isVideoWindowEnabled();

    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const { recordingItems, currentPage, totalCount, retryFetchCounter, isError } = useTivoSelector(
        (state: RootState) => state.recordingActivity,
    );

    const pageItems = useMemo(() => {
        const { items } = getRecordingItemsFromPage(recordingItems, currentPage);
        return items;
    }, [recordingItems, currentPage]);

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.RECORDING_ACTIVITY_SCREEN,
            screenController: ScreenControllerTypes.RECORDING_ACTIVITY_SCREEN,
            taskController: ScreenTaskControllerTypes.NO_TASK,
        },
    });
    const dispatch = useTivoDispatch();

    const onNetworkStateChangeCallback = () => {
        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(resetRecordingActivityState());
        } else {
            getRecordingActivityInitialCall();
        }
    };

    const onCursorStateChangeCallback = (state) => {
        setIsCursorActive(state);
        if (!isCursorActive) dispatch(disableGhostModeRecordingActivity());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const networkManagerRef = NetworkManager.subscribe(onNetworkStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        unsubscribeFromUIOrchestratorRecordingActivity();
        return () => {
            CursorManager.unsubscribe(refCursorManager);
            NetworkManager.unsubscribe(networkManagerRef);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRecordingActivity = async (offset: number, count: number) => {
        Log.i(TAG, 'Fetching recording activity items content...');
        dispatch(setRecordingActivityApiErrorAction(false));

        try {
            const tivoNetworkRequest = new NdvrInteractor().getRecordingHistory(offset, count);

            const response = await tivoNetworkRequest.send();

            if (!response) {
                Log.w(
                    TAG,
                    'No recording history list items found for the given parameters.' +
                        `offset: ${offset}, count: ${count}`,
                );
            }

            return {
                ...response,
                isError: false,
            };
        } catch (error) {
            handleRecordingActivityError(dispatch, error);
            return {
                historyItems: [],
                totalCount: 0,
                isError: true,
            };
        }
    };

    const getRecordingActivityInitialCall = async () => {
        if (recordingItems.length > 0) {
            // When returning from program screen, we want to maintain the current list
            Log.i(TAG, 'Recording items already fetched.');
            return;
        }
        const response = await fetchRecordingActivity(0, LIST_ROWS_ON_PAGE * 2);

        if ((!response || response.totalCount === 0) && !response.isError) {
            Log.i(TAG, 'No recording activity items available.');
            dispatch(showEmptyRecordingActivityScreen());
            return;
        }

        dispatch(setRecordingActivityDataAction(response.historyItems));
        dispatch(setTotalCountRecordingActivityAction(response.totalCount));
    };

    const getRecordingActivityNextPageCall = async () => {
        const response = await fetchRecordingActivity(recordingItems.length, LIST_ROWS_ON_PAGE * 2);

        if (response?.historyItems?.length) {
            const newItems = [...recordingItems, ...response.historyItems];
            dispatch(setRecordingActivityDataAction(newItems));
        }
    };

    useEffect(() => {
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        getRecordingActivityInitialCall();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentPage === 0) return;

        const totalPagesLoaded = Math.ceil(recordingItems.length / LIST_ROWS_ON_PAGE);
        const shouldFetchNextPage =
            currentPage === totalPagesLoaded - 1 &&
            recordingItems.length < totalCount &&
            recordingItems.length < LIST_ITEMS_MAX_COUNT;
        if (shouldFetchNextPage) {
            getRecordingActivityNextPageCall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        if (isError && retryFetchCounter !== 0) {
            getRecordingActivityInitialCall();
        }
    }, [retryFetchCounter]);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.RECORDING_ACTIVITY_TITLE)}>
            <div className="recording-activity-screen">
                <div className="recording-activity-content-container">
                    <RecordingActivityContent items={pageItems} currentPage={currentPage} />

                    <div
                        className={`recording-activity-preview-container ${!isVideoWindowEnabled ? 'video-window-off' : ''}`}
                    >
                        <Mousetrap value="recording-activity-preview">
                            <div
                                className={`recording-activity-preview-trap-height ${!isVideoWindowEnabled ? 'video-window-off' : ''}`}
                            >
                                <RecordingActivityPreview />
                            </div>
                        </Mousetrap>
                    </div>
                </div>
                {isAntiBurnOsdActive && <AntiburnOsd />}
                <Footer shouldShowRightMessage={recordingItems.length > 0} />
                {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                    <RecordingActivityInteractiveLayer />
                ) : null}
                <NoNetworkOverlay />
            </div>
        </SettingsTemplate>
    );
};

export default RecordingActivity;
